import { FetchError } from "@selector/shared";
import { downloadPreparedFile, isFrontendOnlyColumnId } from "@selector/shared/jsx";
import {
  FastaHeaderFormatEnum,
  TableExportFormat,
  TableRequestIdType,
  TableTypeEnum,
  TrackEnum,
} from "@selector/shared/openapi";
import pluralize from "pluralize";
import { uniq } from "ramda";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import type { OverlayTriggerState } from "react-stately";
import { createRandomFileKey, sanitizeFilenameBase } from "widgets/utils";

import {
  exportRanges,
  exportTable,
  type RegionExportRequest,
  type TableExportRequest,
} from "../../../api/export/export-features-or-ranges.ts";
import { syntenyViewerDefaultTracks } from "../../../settings.ts";
import { useGenomeViewerStore } from "../../../store/store.ts";
import type { FeaturesGroups } from "../../../types.ts";
import { getTableTypeFromTrackType } from "../../../utils/type-conversion.ts";
import type { TableRequest } from "../../table/column-templates/ColumnTemplatesPanel.tsx";
import {
  type ContentOption,
  type ExportFormValues,
  FASTA_HEADERS_OPTIONS,
  FORMAT_FEATURES_OPTIONS,
  FORMAT_REGION_OPTIONS,
  getExtensionFromFormat,
  getFeatureContentOption,
  getGffTrackTypeKey,
  getMatchingGffFeatureType,
  GFF_FEATURE_TYPES,
  type GffTrackType,
  type Option,
} from "./utils.ts";

function useContentOptions() {
  const getFeaturesGroups = useGenomeViewerStore((state) => state.getFeaturesGroups);
  const getSelectedRange = useGenomeViewerStore((state) => state.getSelectedRange);
  const selectedRange = useGenomeViewerStore((state) => state.selectedRange);
  const trackGroups = useGenomeViewerStore((state) => state.trackGroups);

  const buildContentOptions = useCallback(() => {
    const opts: ContentOption[] = [];

    // Compute the "selected" region options
    const rangePerContigs = getSelectedRange(); // If a range is selected returns selectedRange, else the visible region

    if (rangePerContigs && selectedRange) {
      opts.push({
        label: `${rangePerContigs.length} Selected ${pluralize("Region", rangePerContigs.length)}`,
        id: "selected_region",
        rangePerContigs,
      });
    }

    // Compute the features export options
    const featuresMap: FeaturesGroups = getFeaturesGroups();

    featuresMap.forEach((groups, trackType) => {
      groups.forEach(({ subType, featureIds }) => {
        if (featureIds.length > 0 && trackType !== TrackEnum.SEQUENCE) {
          opts.push(getFeatureContentOption(trackType, subType, featureIds));
        }
      });
    });

    // Compute the "visible" region options (at the end)
    if (rangePerContigs && !selectedRange) {
      const visibleCount = [...trackGroups.values()].filter((group) => group.graphics.isVisible).length;

      opts.push({
        label: `Visible ${pluralize("Region", visibleCount)}`,
        id: "visible_region",
        rangePerContigs,
      });
    }

    return opts;
  }, [getFeaturesGroups, getSelectedRange, selectedRange, trackGroups]);

  return { buildContentOptions };
}

function useGffFeatureOptions() {
  const tracks = useGenomeViewerStore((state) => state.tracks);

  const buildGffFeatureOptions = useCallback(() => {
    const featureOptions = new Map<string, GffTrackType>();
    const uniqueTracks = uniq(
      [...tracks.values()]
        .filter((track) => track.isVisible)
        .map((track) => ({ trackType: track.trackType, subType: track.subType })),
    );

    uniqueTracks.forEach((track) => {
      const featureType: GffTrackType = {
        name: track.subType?.name ?? syntenyViewerDefaultTracks.find((t) => t.type === track.trackType)?.name ?? "",
        trackType: track.trackType,
        subTypeId: track.subType?.id,
      };
      const featureTypeKey = getGffTrackTypeKey(featureType);
      const matchingGffFeatureType = getMatchingGffFeatureType(track);

      if (matchingGffFeatureType) {
        featureOptions.set(featureTypeKey, { ...featureType, name: matchingGffFeatureType });

        // the track mixes the exotic RNAs among the other transcripts, so we have to enable them with the mRNAs
        if (matchingGffFeatureType === "mRNA") {
          const additionalTypes: GffTrackType[] = [
            { name: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_NCRNA, trackType: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_NCRNA },
            { name: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_LNCRNA, trackType: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_LNCRNA },
            { name: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_SNORNA, trackType: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_SNORNA },
            { name: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_SNRNA, trackType: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_SNRNA },
            { name: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_RRNA, trackType: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_RRNA },
            { name: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_TRNA, trackType: GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_TRNA },
          ];

          for (const additionalType of additionalTypes) {
            featureOptions.set(getGffTrackTypeKey(additionalType), additionalType);
          }
        }
      } else if (
        track.subType?.id &&
        (track.trackType === TrackEnum.AA_SEQUENCE_FEATURE || track.trackType === TrackEnum.NUCLEOTIDE_FEATURE)
      ) {
        featureOptions.set(featureTypeKey, {
          ...featureType,
          name: `${featureType.name} (${track.trackType === TrackEnum.AA_SEQUENCE_FEATURE ? "protein feature" : "DNA feature"})`,
        });
      }
    });

    return featureOptions;
  }, [tracks]);

  return { buildGffFeatureOptions };
}

export const useExportForm = () => {
  const { control, getValues, setValue } = useFormContext<ExportFormValues>();
  const { buildContentOptions } = useContentOptions();
  const { buildGffFeatureOptions } = useGffFeatureOptions();

  const contentSelect = useWatch({ control, name: "contentSelect" });
  const contentOptionMeta = useWatch({ control, name: "contentOptionMeta" });
  const formatSelect = useWatch({ control, name: "formatSelect" });
  const isCompressed = useWatch({ control, name: "compressed" });
  const isReverse = useWatch({ control, name: "reverse" });
  const fileNameValue = useWatch({ control, name: "fileName" });

  const deriveUI = useCallback(
    (v: Pick<ExportFormValues, "contentSelect" | "formatSelect">) => {
      // Content select
      const contentOptions = buildContentOptions();
      const contentValue = contentOptions.find((o) => o.id === v.contentSelect);
      const contentValueIsValid = !!v.contentSelect && contentValue != undefined;

      // Format select
      const showFormatSelect = contentValueIsValid;
      const formatOptions: Option<TableExportFormat>[] =
        contentValue?.trackType != undefined
          ? (contentValue.featureIds?.length ?? 0) > 1
            ? FORMAT_FEATURES_OPTIONS.filter((o) => o.value !== TableExportFormat.RAW) // Raw Sequence (without headers) is only available for sequences or single feature selection
            : FORMAT_FEATURES_OPTIONS
          : FORMAT_REGION_OPTIONS; // Extend later if there is more kind of content options

      // Fasta headers select
      const showFastaHeaderSelect = v.formatSelect === TableExportFormat.FASTA;
      const fastaHeaderOptions = showFastaHeaderSelect
        ? contentValue?.trackType != undefined
          ? FASTA_HEADERS_OPTIONS
          : FASTA_HEADERS_OPTIONS.filter((o) => o.value !== FastaHeaderFormatEnum.VISIBLE_COLUMNS)
        : [];

      // Compressed checkbox
      const showCompressedCheckbox = !!v.formatSelect && v.formatSelect !== TableExportFormat.EXCEL_XLSX;

      // Reversed complement checkbox
      const showReverseCheckbox =
        (v.formatSelect === TableExportFormat.FASTA || v.formatSelect === TableExportFormat.RAW) &&
        contentValue &&
        (!!contentValue.rangePerContigs ||
          contentValue.trackType === TrackEnum.TRANSCRIPT_LOCATION ||
          contentValue.trackType === TrackEnum.GENE ||
          contentValue.trackType === TrackEnum.NUCLEOTIDE_FEATURE);

      // Feature types select
      const showFeatureTypeSelect = false; // TODO Wait for GBK implementation v.formatSelect === TableExportFormat.GBK;
      const featureTypesOptions = showFeatureTypeSelect ? buildGffFeatureOptions() : new Map<string, GffTrackType>();

      return {
        contentOptions,
        contentValueIsValid,

        showFormatSelect,
        formatOptions,

        showFastaHeaderSelect,
        fastaHeaderOptions,

        showCompressedCheckbox,
        showReverseCheckbox,

        showFeatureTypeSelect,
        featureTypesOptions,
      };
    },
    [buildContentOptions, buildGffFeatureOptions],
  );
  const ui = useMemo(() => deriveUI({ contentSelect, formatSelect }), [contentSelect, formatSelect, deriveUI]);

  // Keep a flag to avoid overwriting user edits later
  const fileNameWasAutoRef = useRef<boolean>(true);
  const lastGeneratedRef = useRef<string>("");

  const generateFileName = useCallback(() => {
    // Base parts
    const baseParts: string[] = [];

    const featureCount = contentOptionMeta?.featureIds?.length ?? 0;
    const regionCount = contentOptionMeta?.rangePerContigs?.length ?? 0;

    if (contentOptionMeta?.trackType) {
      const featureTypeName =
        syntenyViewerDefaultTracks.find((t) => t.type === contentOptionMeta?.trackType)?.name ??
        contentOptionMeta.subType?.name ??
        "";
      baseParts.push(`${pluralize(featureTypeName.toLowerCase(), featureCount)}`);
    } else if (contentOptionMeta?.id === "selected_region") {
      baseParts.push(`selected_${pluralize("region", regionCount)}`);
    } else if (contentOptionMeta?.id === "visible_region") {
      baseParts.push(`visible_${pluralize("region", regionCount)}`);
    } else {
      // Fallback to content id if unknown
      if (contentOptionMeta?.id) baseParts.push(contentOptionMeta.id);
    }

    if (isReverse) {
      baseParts.push("_rc");
    }

    baseParts.push("export");

    return sanitizeFilenameBase(baseParts.filter(Boolean).join("_")) || "export";
  }, [contentOptionMeta, isReverse]);

  // Compute the reactive suffix
  const suffixLabel = useMemo(() => {
    const suffix = `${getExtensionFromFormat(formatSelect ?? "", contentOptionMeta?.trackType)}${isCompressed ? ".gz" : ""}`;
    // setValue("suffixName", suffix);
    return suffix;
  }, [formatSelect, contentOptionMeta, isCompressed]);

  useEffect(() => {
    setValue("suffixName", suffixLabel, { shouldDirty: false, shouldValidate: true });
  }, [suffixLabel, setValue]);

  useEffect(() => {
    // Content select must be allowed
    const contentValue = getValues("contentSelect");
    const contentValueIsValid = !!contentValue && ui.contentOptions.some((o) => o.id === contentValue);

    if (!contentValueIsValid) {
      setValue("contentSelect", undefined);
    }

    // Format select must be an allowed option (depends on content value)
    const formatValue = getValues("formatSelect");
    const formatValueIsValid = !!formatValue && ui.formatOptions.some((o) => o.value === formatValue);

    if (!formatValueIsValid) {
      setValue("formatSelect", undefined);
    }

    // Fasta header select only when allowed
    if (!ui.showFastaHeaderSelect) {
      setValue("fastaHeaderSelect", undefined);
    }

    // Compressed checkbox only when allowed
    if (!ui.showCompressedCheckbox) {
      setValue("compressed", undefined);
    }

    // Reverse complement checkbox only when allowed
    if (!ui.showReverseCheckbox) {
      setValue("reverse", undefined);
    }

    // Fasta header select only when allowed
    if (!ui.showFeatureTypeSelect) {
      setValue("featureTypes", undefined);
    }
  }, [getValues, setValue, ui]);

  // Ensure contentSelect is always valid and defaults to first option
  useEffect(() => {
    const current = getValues("contentSelect");
    const first = ui.contentOptions[0]?.id;

    // If no current or current no longer valid, pick the first
    const currentIsValid = !!current && ui.contentOptions.some((o) => o.id === current);

    if (!currentIsValid && first) {
      const meta = ui.contentOptions.find((o) => o.id === first);
      setValue("contentOptionMeta", meta);
      setValue("contentSelect", first);
    }
  }, [ui.contentOptions, getValues, setValue]);

  // Ensure formatSelect follows contentSelect and is valid
  useEffect(() => {
    if (!ui.showFormatSelect) {
      setValue("formatSelect", undefined);
      return;
    }

    const current = getValues("formatSelect");
    const first = ui.formatOptions[0]?.value;
    const currentIsValid = !!current && ui.formatOptions.some((o) => o.value === current);

    if (!currentIsValid && first) {
      setValue("formatSelect", first);
    }
  }, [ui.showFormatSelect, ui.formatOptions, contentSelect, getValues, setValue]);

  // FASTA header select validity
  useEffect(() => {
    if (ui.showFastaHeaderSelect) {
      const current = getValues("fastaHeaderSelect");
      const first = ui.fastaHeaderOptions[0]?.value;
      const currentIsValid = !!current && ui.fastaHeaderOptions.some((o) => o.value === current);

      if (!currentIsValid && first) {
        setValue("fastaHeaderSelect", first);
      }
    } else {
      setValue("fastaHeaderSelect", undefined);
    }
  }, [ui.showFastaHeaderSelect, ui.fastaHeaderOptions, formatSelect, getValues, setValue]);

  // Compressed checkbox validity with default=true when shown
  useEffect(() => {
    if (ui.showCompressedCheckbox) {
      const current = getValues("compressed");

      if (current === undefined) {
        setValue("compressed", true);
      }
    } else {
      setValue("compressed", undefined);
    }
  }, [ui.showCompressedCheckbox, getValues, setValue]);

  // Reverse complement checkbox validity with default=false when shown
  useEffect(() => {
    if (ui.showReverseCheckbox) {
      const current = getValues("reverse");

      if (current === undefined) {
        setValue("reverse", false);
      }
    } else {
      setValue("reverse", undefined);
    }
  }, [ui.showReverseCheckbox, getValues, setValue]);

  useEffect(() => {
    const current = (getValues("fileName") ?? "").trim();
    const next = generateFileName();

    // auto-manage only if empty or still equal to the last generated value
    if (fileNameWasAutoRef.current && current !== next) {
      setValue("fileName", next, { shouldDirty: false, shouldValidate: true });
      lastGeneratedRef.current = next;
      fileNameWasAutoRef.current = true;
    }
  }, [generateFileName, getValues, setValue]);

  // Runs whenever the user changes the filename
  useEffect(() => {
    const current = (fileNameValue ?? "").trim();
    const lastAuto = (lastGeneratedRef.current ?? "").trim();
    // If user's value differs from the most recent generated name,
    // consider it user-edited and stop auto-overwriting.
    fileNameWasAutoRef.current = current === lastAuto;
  }, [fileNameValue]);

  return {
    ui,
    control,
    suffixLabel,
    setValue,
  };
};

export const useExportPopover = (state: OverlayTriggerState) => {
  const gridApiRef = useGenomeViewerStore((state) => state.gridApiRef);
  const trackGroups = useGenomeViewerStore((state) => state.trackGroups);
  const [isLoading, setIsLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get("viewid") != null;

  const { handleSubmit, formState, trigger, setError, clearErrors } = useFormContext<ExportFormValues>();

  const isFormInvalid = !formState.isValid;

  /** Helper: build region export payload */
  const buildRegionExportPayload = (form: ExportFormValues): RegionExportRequest | null => {
    const { contentOptionMeta, fileName, fastaHeaderSelect, formatSelect, reverse, compressed } = form;
    const ranges = contentOptionMeta?.rangePerContigs?.map((r) => ({
      contigId: r.contigId,
      range: { start: r.start, stop: r.stop },
    }));

    if (!fileName || !formatSelect || !ranges?.length) return null;

    return {
      compressed: compressed ?? true,
      fileName,
      headerFormat: fastaHeaderSelect ?? FastaHeaderFormatEnum.DEFAULT,
      regions: ranges,
      reverseComplement: reverse ?? false,
      tableExportFormat: formatSelect,
    };
  };

  /** Helper: build table export payload */
  const buildTableExportPayload = (form: ExportFormValues): TableExportRequest | null => {
    const colIds =
      gridApiRef.current
        ?.getColumns()
        ?.map((colDef) => ({ colId: colDef.getColId() }))
        .filter(({ colId }) => !isFrontendOnlyColumnId(colId)) ?? [];

    const { contentOptionMeta, fileName, fastaHeaderSelect, formatSelect, compressed, reverse } = form;
    if (!contentOptionMeta || !fileName || !formatSelect) return null;

    const { featureIds, subType, trackType } = contentOptionMeta;
    if (!trackType) return null;

    const ids = [...trackGroups.values()].map(({ organismId }) => organismId);

    const table: TableRequest = {
      idType: TableRequestIdType.ENTITY,
      subTypeId: subType?.id,
      resultType: getTableTypeFromTrackType(trackType),
      queryType: TableTypeEnum.ORGANISM,
      ids,
      refGenomeId: undefined,
      endRow: 0,
      startRow: 0,
      sortModel: [],
      filterModel: {},
      content: [],
      selectedIds: featureIds,
      valueCols: isViewMode ? colIds : [],
    };

    return {
      compressed: formatSelect === TableExportFormat.EXCEL_XLSX ? false : (compressed ?? true),
      fileName,
      format: formatSelect,
      headerFormat: fastaHeaderSelect ?? FastaHeaderFormatEnum.RAW,
      reverseComplement: reverse ?? false,
      table,
    };
  };

  const downloadIfOkAndClose = (status: number, fileKey: string) => {
    if (status === 202) {
      // 202 responses indicate async export; notifications will handle the download.
      state.close();
      return;
    }

    if (status !== 200) {
      throw new FetchError(status, "No data has been returned from the server");
    }

    downloadPreparedFile(fileKey);
    state.close();
  };

  const onSubmit = async (form: ExportFormValues) => {
    clearErrors("root");
    setIsLoading(true);

    const fileKey = createRandomFileKey();

    try {
      if (form.contentOptionMeta?.rangePerContigs?.length) {
        // Range export
        const payload = buildRegionExportPayload(form);

        if (!payload) {
          setError("root", { message: "Some selections are missing. Please review the form and try again." });
          return;
        }

        const response = await exportRanges(payload, fileKey);
        downloadIfOkAndClose(response.status, fileKey);
      } else {
        // Table export
        const payload = buildTableExportPayload(form);

        if (!payload) {
          setError("root", { message: "Some selections are missing. Please review the form and try again." });
          return;
        }

        const response = await exportTable(payload, fileKey);
        downloadIfOkAndClose(response.status, fileKey);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unexpected export error.";
      setError("root", { message });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (state.isOpen) {
      // Validate current values so formState.isValid is accurate on open
      void trigger();
    }
  }, [state.isOpen, trigger]);

  const onExportSubmit = () => handleSubmit(onSubmit)();

  return { isFormInvalid, isLoading, onExportSubmit, errorMessage: formState.errors.root?.message };
};
